import { useState } from 'react';
import { pbToast } from '../toast';
import { FormFields, FormFooter, initialValuesFromFields } from './FormFields';
import type { FieldSpec } from './FormFields';
import type { OrgChartDoc, Presence } from '../types';

const PRESENCE_OPTIONS: { value: Presence; label: string }[] = [
  { value: 'available', label: 'Available' },
  { value: 'busy', label: 'Busy' },
  { value: 'away', label: 'Away' },
  { value: 'offline', label: 'Offline' },
];

const SUN_R_MIN = 40;
const SUN_R_MAX = 260;

function buildFields(doc: OrgChartDoc): FieldSpec[] {
  const pm = doc.org.pm || {} as OrgChartDoc['org']['pm'];
  const dpm = doc.org.dpm || {} as OrgChartDoc['org']['dpm'];
  return [
    { key: 'code', label: 'Contract / code line', type: 'text', value: doc.org.code || '' },

    { section: 'Program Manager' },
    { key: 'pmName', label: 'Name', type: 'text', value: pm.name || '', half: true },
    { key: 'pmRole', label: 'Title', type: 'text', value: pm.role || '', half: true },
    { key: 'pmExt', label: 'Extension', type: 'text', value: pm.ext || '', half: true },
    {
      key: 'pmPres', label: 'Presence', type: 'select', value: pm.presence || 'available', half: true,
      options: PRESENCE_OPTIONS,
    },

    { section: 'Deputy Program Manager' },
    { key: 'dpmName', label: 'Name', type: 'text', value: dpm.name || '', half: true },
    { key: 'dpmRole', label: 'Title', type: 'text', value: dpm.role || '', half: true },
    { key: 'dpmExt', label: 'Extension', type: 'text', value: dpm.ext || '', half: true },
    {
      key: 'dpmPres', label: 'Presence', type: 'select', value: dpm.presence || 'available', half: true,
      options: PRESENCE_OPTIONS,
    },

    { section: 'Centre disc' },
    {
      key: 'sunR', label: 'Radius', type: 'number', half: true,
      value: Math.round(doc.settings.sunR), min: SUN_R_MIN, max: SUN_R_MAX,
    },
    { key: 'sx', label: 'X position', type: 'number', value: Math.round(doc.sun.x), half: true },
    { key: 'sy', label: 'Y position', type: 'number', value: Math.round(doc.sun.y), half: true },
  ];
}

interface LeadershipFormValues {
  code: string;
  pmName: string;
  pmRole: string;
  pmExt: string;
  pmPres: Presence;
  dpmName: string;
  dpmRole: string;
  dpmExt: string;
  dpmPres: Presence;
  sunR: number | null;
  sx: number | null;
  sy: number | null;
}

function validate(values: LeadershipFormValues): string[] {
  const errors: string[] = [];
  if (!String(values.code || '').trim()) errors.push('Contract / code line is required.');
  if (!String(values.pmName || '').trim()) errors.push('Program Manager name is required.');
  if (!String(values.dpmName || '').trim()) errors.push('Deputy Program Manager name is required.');
  if (values.sunR == null || Number.isNaN(values.sunR)) {
    errors.push('Centre disc radius is required.');
  } else if (values.sunR < SUN_R_MIN || values.sunR > SUN_R_MAX) {
    errors.push(`Centre disc radius must be between ${SUN_R_MIN} and ${SUN_R_MAX}.`);
  }
  if (values.sx == null || Number.isNaN(values.sx)) errors.push('Centre disc X position is required.');
  if (values.sy == null || Number.isNaN(values.sy)) errors.push('Centre disc Y position is required.');
  return errors;
}

interface LeadershipEditorProps {
  doc: OrgChartDoc;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: (action: any) => void;
  closeModal: () => void;
}

export default function LeadershipEditor({ doc, dispatch, closeModal }: LeadershipEditorProps) {
  const [values, setValues] = useState(
    () => initialValuesFromFields(buildFields(doc)) as unknown as LeadershipFormValues,
  );
  const onChange = (key: string, value: boolean | number | null | string) =>
    setValues((v) => ({ ...v, [key]: value } as unknown as LeadershipFormValues));

  const onSave = () => {
    const errors = validate(values);
    if (errors.length) {
      pbToast(errors.join('\n'), 'err');
      return;
    }
    dispatch({
      type: 'UPDATE_LEADERSHIP',
      code: values.code.trim(),
      pm: {
        name: values.pmName.trim(),
        role: (values.pmRole || '').trim(),
        ext: (values.pmExt || '').trim(),
        presence: values.pmPres,
      },
      dpm: {
        name: values.dpmName.trim(),
        role: (values.dpmRole || '').trim(),
        ext: (values.dpmExt || '').trim(),
        presence: values.dpmPres,
      },
      sunR: values.sunR,
      sunX: values.sx,
      sunY: values.sy,
    });
    pbToast('Leadership updated', 'ok');
    closeModal();
  };

  return (
    <>
      <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--mute2)', lineHeight: 1.5 }}>
        The centre disc of the chart.
      </p>
      <FormFields fields={buildFields(doc)} values={values as unknown as Record<string, boolean | number | null | string>} onChange={onChange} idPrefix="lead-" />
      <FormFooter saveLabel="Save changes" onCancel={() => closeModal()} onSave={onSave} />
    </>
  );
}
